/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { View, Icon, Pressable } from '@components/atoms'
import dayjs from 'dayjs'
import { IconButton, Tooltip } from '@mui/material'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'
import { ProductType } from '@schemas'

interface ProductListItemProps extends ProductType {
	onClick?: () => void
	onDelete?: () => void
	onEdit?: () => void
	isSelected?: boolean
}

/**
 * 제품 목록의 한 행 — 헤어라인 인덱스 행. 선택 = 좌측 2px ink 룰 + surface.
 * 카드 그림자·보라색 강조는 걷어냈다. 상태는 칩 채움 단계(사용중 outline · 미사용 faint).
 */
export const ProductListItem = ({
	code,
	name: productName,
	usd: price,
	useYn: isActive,
	createdAt,
	onClick,
	onDelete,
	onEdit,
	isSelected = false,
}: ProductListItemProps) => {
	return (
		<View css={style} className={isSelected ? 'selected' : ''}>
			<Pressable className="productItemButton" onClick={onClick}>
				<View className="header">
					<span className="title">{productName}</span>
					{code !== '' && <span className="productCode">{code}</span>}
					<span className={`boChip ${isActive ? 'outline' : 'faint'}`}>
						{isActive ? '사용중' : '미사용'}
					</span>
				</View>

				<View className="infoRow">
					<span className="boSpecLabel">가격</span>
					<span className="infoValue">
						{price.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
						<span className="infoSub">
							{` (${(price / 1.1).toLocaleString('en-US', {
								style: 'currency',
								currency: 'USD',
							})})`}
						</span>
					</span>
				</View>
				<View className="infoRow">
					<span className="boSpecLabel">제작일자</span>
					<span className="infoValue">{dayjs(createdAt).format('YYYY-MM-DD')}</span>
				</View>
			</Pressable>

			<View className="buttonContainer">
				<Tooltip title="수정">
					<IconButton
						size="small"
						onClick={e => {
							e.stopPropagation()
							onEdit?.()
						}}
					>
						<Icon name="pencil-outline" style={{ fontSize: 17, color: 'inherit' }} />
					</IconButton>
				</Tooltip>
				<Tooltip title="삭제">
					<IconButton
						size="small"
						onClick={e => {
							e.stopPropagation()
							onDelete?.()
						}}
					>
						<Icon name="trash-can-outline" style={{ fontSize: 17, color: 'inherit' }} />
					</IconButton>
				</Tooltip>
			</View>
		</View>
	)
}

const style = css({
	...boShared,
	position: 'relative',
	display: 'flex',
	alignItems: 'flex-start',
	borderBottom: `1px solid ${mono.line}`,
	borderLeft: '2px solid transparent',
	transition: 'background-color 200ms ease-out, border-color 200ms ease-out',

	'&:hover': { background: mono.surface },
	'&.selected': {
		background: mono.surface,
		borderLeftColor: mono.ink,
	},

	'.productItemButton': {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		width: '100%',
		padding: '16px 14px',
		textAlign: 'left',
		borderRadius: 0,
	},

	'.header': {
		display: 'flex',
		gap: 10,
		alignItems: 'center',
		minWidth: 0,
		paddingRight: 64,

		'.title': {
			fontSize: 17,
			fontWeight: 400,
			color: mono.ink,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		'.productCode': {
			fontSize: 11,
			letterSpacing: '.14em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
			flexShrink: 0,
		},
	},

	'.infoRow': {
		display: 'flex',
		alignItems: 'baseline',
		gap: 12,
		'.boSpecLabel': { width: 64, flexShrink: 0 },
		'.infoValue': {
			fontSize: 13,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums',
		},
		'.infoSub': { color: mono.faint },
	},

	'.buttonContainer': {
		position: 'absolute',
		top: 12,
		right: 8,
		display: 'flex',
		gap: 2,
		opacity: 0,
		transition: 'opacity 200ms ease-out',
	},
	'&:hover .buttonContainer, &.selected .buttonContainer': { opacity: 1 },
})
